import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
// Served from our own bundle, not Builder.io's CDN. See the note at the <img>.
// PNG, not WebP: Amplify's SPA rewrite has an extension allowlist that does
// not include webp, so the hosted .webp URL returned index.html and the
// wordmark rendered as broken alt text on every page. Same pixels.
// Brand refresh R5 (2026-09-04): the rev-2 wordmark — Hanken Grotesk outlines,
// one ink, teal tittles on all four i's. SVG outlines, so no font dependency
// and no Amplify rewrite-allowlist concern (svg is on the allowlist).
import optimicitiWordmark from "assets/img/brand/optimiciti_wordmark_refresh.svg";
import { BugDialog } from "components/common/dialogs";
import { INDUSTRIES } from "components/domain/industries/industriesData.js";

const Footer = () => {
  const [bugDialogOpen, setBugDialogOpen] = React.useState(false);

  return (
    <Box component="footer" sx={{ pt: 4, pb: 2, backgroundColor: "#FFFFFF", borderTop: "1px solid", borderColor: "divider", width: '100%' }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 3 }} justifyContent="space-between">
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {/* Our own logo, previously loaded from Builder.io's CDN — a
                  third-party host left over from that integration, which the
                  site no longer uses. The file is byte-identical; it just
                  ships with the bundle now, so the logo can't disappear if
                  that account lapses or the asset is deleted. */}
              <img
                src={optimicitiWordmark}
                alt="Optimiciti Logo"
                style={{ height: "24px" }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5, maxWidth: "400px", fontSize: "0.8125rem" }}>
              Route and schedule planning for fleets — from a handful of stops to a few hundred.
            </Typography>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "dark.main", mb: 1.5 }}>Industries</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Derived from INDUSTRIES so a new industry page appears here on
                  its own. The list used to be three hardcoded links, which is
                  how the sidebar's example list drifted out of date. */}
              {INDUSTRIES.map((ind) => (
                <Link key={ind.slug} to={`/industries/${ind.slug}`} style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>{ind.navLabel}</Typography></Link>
              ))}
              <Link to="/industries" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>All industries</Typography></Link>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "dark.main", mb: 1.5 }}>Product</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Link to="/use-cases" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>Examples</Typography></Link>
              <Link to="/pricing" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>Pricing</Typography></Link>
              <Link to="/api" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>API Docs</Typography></Link>
              {/* The first link in this app that leaves the domain, so it is an <a> rather
                  than a react-router <Link>: a <Link to="https://..."> does not navigate, it
                  pushes the URL onto this app's history and renders the 404 route. rel is
                  noopener because target is _blank, and the styling is copied from its
                  siblings on purpose so one column does not acquire a second look. */}
              <a
                href="https://www.waymatrix.io"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>WayMatrix routing API</Typography>
              </a>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "dark.main", mb: 1.5 }}>Company</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Link to="/careers" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>Careers</Typography></Link>
              <Box
                component="button"
                type="button"
                onClick={() => setBugDialogOpen(true)}
                sx={{
                  color: "text.secondary",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  "&:hover": { color: "primary.main" },
                  background: "none",
                  border: "none",
                  p: 0,
                  font: "inherit",
                  textAlign: "left",
                  textDecoration: "none",
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: "2px",
                    borderRadius: "2px",
                  },
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>
                  Support & Feedback
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "dark.main", mb: 1.5 }}>Legal</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Link to="/privacy-policy" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>Privacy</Typography></Link>
              <Link to="/terms-of-service/" style={{ textDecoration: "none", color: "inherit" }}><Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.8125rem", "&:hover": { color: "primary.main" } }}>Terms</Typography></Link>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
            © {new Date().getFullYear()} Optimiciti. All rights reserved.
          </Typography>
        </Box>
      </Container>
      <BugDialog open={bugDialogOpen} handleClose={() => setBugDialogOpen(false)} />
    </Box>
  );
};

export default Footer;
